import { BEE_PER_CELL, hiveStageFor, hiveStages } from '../data/engagement'
import { getRecapConfig } from '../hooks/recapConfigStore'
import type {
  BeeArchetypeId,
  HiveStage,
  Recap,
  RecapCommunityContext,
  RecapHabit,
  RecapModule,
} from '../types/engagement'

/**
 * Recap engine (prototype). One engine for monthly and annual recaps — only the period differs.
 * Production: the backend aggregates the period and stores the generated result (Marco 3).
 */

/** Always reads the live shared recap configuration (data/recapConfig.ts + Admin edits). */
const recapConfig = () => getRecapConfig()

export function rankedHabits(recap: Recap): { habit: RecapHabit; ratio: number }[] {
  return (Object.entries(recap.habitConsistency) as [RecapHabit, number][])
    .map(([habit, ratio]) => ({ habit, ratio }))
    .sort((a, b) => b.ratio - a.ratio)
}

/** Habits tied for the top spot (within the configured tolerance). */
export function strongestHabits(recap: Recap): RecapHabit[] {
  const ranked = rankedHabits(recap)
  const top = ranked[0]?.ratio ?? 0
  return ranked
    .filter((item) => top - item.ratio <= recapConfig().archetypeTieTolerancePct / 100)
    .map((item) => item.habit)
}

/**
 * Bee type from Daily Check completion consistency only.
 * Tie at the top or an even spread → balanced; never an arbitrary winner.
 */
export function selectBeeArchetype(recap: Recap): BeeArchetypeId {
  const ranked = rankedHabits(recap)
  if (ranked.length === 0) return 'balanced'
  const spread = ranked[0].ratio - ranked[ranked.length - 1].ratio
  if (spread <= recapConfig().archetypeBalancedSpreadPct / 100) return 'balanced'
  if (strongestHabits(recap).length > 1) return 'balanced'
  return ranked[0].habit
}

export interface HiveSnapshot {
  bee: number
  cells: number
  stage: HiveStage
}

/** Hive state from a historical cumulative-Bee snapshot, using the configured thresholds. */
export function hiveSnapshot(bee: number): HiveSnapshot {
  return { bee, cells: Math.floor(bee / BEE_PER_CELL), stage: hiveStageFor(bee).stage }
}

/** Grid size that fits the end snapshot (next stage threshold, or the end stage itself). */
export function hiveGridCells(end: HiveSnapshot): number {
  const next = hiveStageFor(end.bee).next
  const ceiling = next?.minBee ?? end.stage.minBee ?? hiveStages[hiveStages.length - 1].minBee
  return Math.max(end.cells, Math.ceil(ceiling / BEE_PER_CELL), 1)
}

export function socialTotal(recap: Recap): number {
  return recap.socialHighlight ? recap.socialHighlight.likes + recap.socialHighlight.comments : 0
}

/** "Quem esteve com você" only appears with meaningful public interaction data. */
export function hasSocialHighlight(recap: Recap): boolean {
  return socialTotal(recap) >= recapConfig().minSocialInteractions
}

export function percentileEligible(context: RecapCommunityContext | undefined): boolean {
  return !!context && context.eligibleMembers >= recapConfig().minCommunityPopulation
}

export type RecapCardId =
  | 'intro'
  | 'beeType'
  | 'hive'
  | 'consistency'
  | 'habit'
  | 'activity'
  | 'community'
  | 'social'
  | 'percentile'
  | 'milestones'
  | 'share'

/** Ordered story cards for a recap, honouring module toggles and data availability. */
export function recapCards(recap: Recap): RecapCardId[] {
  const modules = recapConfig().modules
  const sequence: { id: RecapCardId; module?: RecapModule; show?: boolean }[] = [
    { id: 'intro' },
    { id: 'beeType', module: 'beeType' },
    { id: 'hive', module: 'hive' },
    { id: 'consistency', module: 'consistency' },
    { id: 'habit', module: 'habit' },
    { id: 'activity', module: 'activity' },
    { id: 'community', module: 'community' },
    { id: 'social', module: 'social', show: hasSocialHighlight(recap) },
    { id: 'percentile', module: 'percentile', show: recap.communityContext.length > 0 },
    { id: 'milestones', module: 'milestones', show: recap.period === 'year' && recap.highlights.length > 0 },
    { id: 'share' },
  ]
  return sequence
    .filter((card) => (card.module ? modules[card.module] : true) && card.show !== false)
    .map((card) => card.id)
}
